import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { api, extractApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth-context";
import { canCreateOrEditCRM, canSeeArchivedCRM } from "../lib/crm-permissions";
import PageHeader from "../components/layout/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import EmptyState from "../components/empty/EmptyState";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { formatDate } from "../lib/format";

export default function CompaniesPage() {
  const navigate = useNavigate();
  const { role, isSuperAdmin } = useAuth();
  const perm = { role, isSuperAdmin };
  const canCreate = canCreateOrEditCRM(perm);
  const canSeeArchived = canSeeArchivedCRM(perm);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({
    name: "",
    customer_number: "",
    external_reference: "",
    website: "",
    industry: "",
    size: "",
    address: "",
    source: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const searchRef = useRef(search);
  searchRef.current = search;

  const load = useCallback(async (searchValue = searchRef.current) => {
    setLoading(true);
    try {
      const params = {};
      if (searchValue) params.search = searchValue;
      if (canSeeArchived && showArchived) params.include_archived = true;
      const r = await api.get("/api/companies", { params });
      setRows(r.data);
    } catch (e) {
      toast.error(extractApiError(e, "Firmen konnten nicht geladen werden."));
    } finally {
      setLoading(false);
    }
  }, [canSeeArchived, showArchived]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Bitte einen Firmennamen angeben.");
    setSaving(true);
    try {
      await api.post("/api/companies", form);
      toast.success("Firma erstellt.");
      setForm({
        name: "",
        customer_number: "",
        external_reference: "",
        website: "",
        industry: "",
        size: "",
        address: "",
        source: "",
        notes: "",
      });
      setDialog(false);
      load();
    } catch (err) {
      toast.error(extractApiError(err, "Firma konnte nicht erstellt werden."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Firmen"
        description="Accounts und Firmenkontakte verwalten."
        actions={
          canCreate ? (
            <Button onClick={() => setDialog(true)} data-testid="company-create-button">
              <Plus className="w-4 h-4 mr-1.5" /> Firma erstellen
            </Button>
          ) : null
        }
      />

      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="surface rounded-xl p-3 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="companies-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suche nach Name, Kundennummer, Branche, Website …"
            className="pl-10"
          />
        </div>
        {canSeeArchived ? (
          <div className="mt-3 flex items-center justify-end">
            <label className="inline-flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              Archivierte anzeigen
            </label>
          </div>
        ) : null}
      </form>

      {loading ? (
        <div className="text-sm text-muted-foreground py-8 text-center">Wird geladen …</div>
      ) : rows.length === 0 ? (
        <EmptyState
          title="Noch keine Firmen"
          description="Legen Sie Ihre erste Firma an."
          actionLabel={canCreate ? "Firma erstellen" : undefined}
          onAction={canCreate ? () => setDialog(true) : undefined}
          testId="companies-empty"
        />
      ) : (
        <div data-testid="companies-table" className="obsidian overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 border-b border-border">
                <tr className="text-left text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Kundennummer</th>
                  <th className="px-4 py-3 font-medium">Branche</th>
                  <th className="px-4 py-3 font-medium">Größe</th>
                  <th className="px-4 py-3 font-medium">Website</th>
                  <th className="px-4 py-3 font-medium">Erstellt</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((c) => (
                  <tr key={c.id} onClick={() => navigate(`/firmen/${c.id}`)} className="hover:bg-muted/50 cursor-pointer transition-colors" data-testid={`companies-table-row-${c.id}`}>
                    <td className="px-4 py-3 font-medium">
                      <span className="inline-flex items-center gap-2">
                        {c.name}
                        {c.archived_at ? <Badge variant="secondary">Archiviert</Badge> : null}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{c.customer_number || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{c.industry || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{c.size || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground truncate max-w-[280px]">{c.website || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{formatDate(c.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Firma erstellen</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Fld label="Name *" full><Input data-testid="company-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus /></Fld>
            <Fld label="Kundennummer"><Input value={form.customer_number} onChange={(e) => setForm({ ...form, customer_number: e.target.value })} /></Fld>
            <Fld label="Externe Referenz"><Input value={form.external_reference} onChange={(e) => setForm({ ...form, external_reference: e.target.value })} /></Fld>
            <Fld label="Branche"><Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} /></Fld>
            <Fld label="Größe"><Input value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} placeholder="z.B. 50-200" /></Fld>
            <Fld label="Website" full><Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} /></Fld>
            <Fld label="Adresse" full><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Fld>
            <Fld label="Quelle" full><Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} /></Fld>
            <Fld label="Notizen" full><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Fld>
            <DialogFooter className="md:col-span-2">
              <Button type="button" variant="outline" onClick={() => setDialog(false)}>Abbrechen</Button>
              <Button type="submit" disabled={saving} data-testid="company-form-submit">
                {saving ? "Speichern …" : "Erstellen"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Fld({ label, children, full = false }) {
  return (
    <div className={`space-y-1.5 ${full ? "md:col-span-2" : ""}`}>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
